import moment from 'moment';
import 'moment/locale/es.js';

const t = {
  ES: {
    today: 'Hoy',
    tomorrow: 'Mañana',
    on: 'El',
    from: 'de',
    to: 'a',
    toThe: 'al',
    direct: 'DIRECTO',
    distribution: 'DISTRIBUCIÓN',
    collection: 'RECOGIDA',
    mix: 'MULTI RUTA MEZCLADO',
    CUSTOM_PALLET: 'Palés Mixtos',
    MIXED_PALLET: 'Palés Mixtos',
    US_PALLET: 'Palés americanos',
    EU_PALLET: 'Palés europeos',
    LIFTGATE: 'IMPORTANTE: ES NECESARIA TRAMPILLA',
    SIDE_LOAD: 'IMPORTANTE: ES NECESARIA CARGA LATERAL',
    TOP_LOAD: 'IMPORTANTE: ES NECESARIA CARGA SUPERIOR',
    AIRPORT: 'IMPORTANTE: Recogida en aeropuerto ',
    PICKUP: 'Recogida:',
    DROPOFF: 'Entrega:',
  },
  UK: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    on: 'On',
    from: 'from',
    to: 'to',
    toThe: 'to',
    direct: 'DIRECT',
    distribution: 'DISTRIBUTION',
    collection: 'COLLECTION',
    mix: 'MIXED MULTI ROUTE',
    CUSTOM_PALLET: 'Custom pallets',
    MIXED_PALLET: 'Mixed  pallets',
    US_PALLET: 'Standard pallets',
    EU_PALLET: 'European pallets',
    LIFTGATE: 'ATTENTION: Tail lift required',
    SIDE_LOAD: 'ATTENTION: Curtain side required',
    TOP_LOAD: 'ATTENTION: Retractable roof required',
    AIRPORT:
      'ATTENTION: This is an airport collection. Collection note provided must be printed, filled in and signed before going to the collection point.',
    PICKUP: 'Collection:',
    DROPOFF: 'Drop off:',
  },
  FR: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    on: 'On',
    from: 'from',
    to: 'to',
    toThe: 'to',
    direct: 'DIRECT',
    distribution: 'DISTRIBUTION',
    collection: 'COLLECTION',
    mix: 'MIXED MULTI ROUTE',
    CUSTOM_PALLET: 'Custom pallets',
    MIXED_PALLET: 'Mixed pallets',
    US_PALLET: 'Standard pallets',
    EU_PALLET: 'European pallets',
    LIFTGATE: 'ATTENTION: Tail lift required',
    SIDE_LOAD: 'ATTENTION: Curtain side required',
    TOP_LOAD: 'ATTENTION: Retractable roof required',
    AIRPORT:
      'ATTENTION: This is an airport collection. Collection note provided must be printed, filled in and signed before going to the collection point.',
    PICKUP: 'Collection:',
    DROPOFF: 'Drop off:',
  },
};

/**
 * @param {string} fromTime - date
 * @param {string} toTime - date
 * @param {string} lang - 'uk', 'es', 'fr'
 * @returns {string}
 * Same day & today or tomorrow:    Mañana de 05:00 a 11:00  -> Depends on Language provided
 * Same day:                        El miércoles 27/03 de 08:30 a 12:30
 * Different day & todayTomorrow:   Mañana viernes 15/03 de 08:00 a domingo 17/03 02:00
 * Different day:                   El jueves 21/03 de 02:00 a viernes 22/03 06:15
 */
export const dateFormatter = (fromTime, toTime, lang) => {
  const fromDate = moment(fromTime).locale(lang);
  const toDate = moment(toTime).locale(lang);
  const today = moment();
  const tomorrow = moment().add(1, 'days');

  const isToday = fromDate.isSame(today, 'day');
  const isTomorrow = fromDate.isSame(tomorrow, 'day');
  const isTodayOrTomorrow = isToday || isTomorrow;
  const isSameDay = fromDate.isSame(toDate, 'day');

  const from = {
    hour: fromDate.format('HH:mm'),
    day: fromDate.format('dddd'),
    date: fromDate.format('DD/MM'),
  };

  const to = {
    hour: toDate.format('HH:mm'),
    day: toDate.format('dddd'),
    date: toDate.format('DD/MM'),
  };

  const prefix = isToday ? t[lang].today : isTomorrow ? t[lang].tomorrow : t[lang].on;

  const fromNormal = `${prefix} ${from.day} ${from.date} ${t[lang].from} ${from.hour}`;
  const fromTodayOrTomorrow = `${prefix} ${t[lang].from} ${from.hour}`;

  const toSameDay = `${t[lang].to} ${to.hour}`;
  const toDifferentDay = `${t[lang].to} ${to.day} ${to.date} ${to.hour}`;

  const responses = {
    todayOrTomorrow: `${fromTodayOrTomorrow} ${toDifferentDay}`,
    todatOrTomorrowSameDay: `${fromTodayOrTomorrow} ${toSameDay}`,
    sameDay: `${fromNormal} ${toSameDay}`,
    differentDay: `${fromNormal} ${toDifferentDay}`,
  };

  return (
    (isSameDay && isTodayOrTomorrow && responses.todatOrTomorrowSameDay) ||
    (isSameDay && responses.sameDay) ||
    (isTodayOrTomorrow && responses.isTodayOrTomorrow) ||
    responses.differentDay
  );
};

/**
 * @param {array} stops
 * @param {string} lang
 * @returns {object} -  { PICKUP: 1, DROPOFF: 3, shipmentType: 'DISTRIBUTION' }
 */
export const getShipmentInfoFromStops = (stops, lang) => {
  const counter = getStopTypesCounter(stops);
  return {
    ...counter,
    shipmentType: getShipmentType(stops, lang, counter),
  };
};

/**
 * @param {array} stops
 * @param {string} stops[].stop_type - 'PICKUP' or 'DROPOFF'
 * @returns {Object} - { PICKUP: 1, DROPOFF: 3 }
 */
const getStopTypesCounter = (stops) => {
  return stops.reduce(
    (acc, { stop_type }) => {
      return {
        ...acc,
        [stop_type]: acc[stop_type] + 1,
      };
    },
    { PICKUP: 0, DROPOFF: 0 }
  );
};

/**
 * @param {string} stops[]
 * @param {string} lang
 * @param {Object} counter
 * @returns {String} - 'Distribution'
 */
const getShipmentType = (stops, lang, counter) => {
  return (
    (stops.length === 2 && t[lang].direct) ||
    (counter.PICKUP === 1 && t[lang].distribution) ||
    (counter.DROPOFF === 1 && t[lang].collection) ||
    t[lang].mix
  );
};

/**
 * @param {array} stops
 * @param {string} country - UK, ES, FR,
 * @returns {array} - array with strings for each stop
 */
export const stopRenderer = (stops, country) => {
  return stops.map((stop) => {
    const { address } = stop;
    return `
${t[country][stop.stop_type]}
${address.address_line}
${address.label} - ${address.zipcode}
${dateFormatter(stop.time_from, stop.time_to, country)}
${getPalletsByStop(stop, country)}
    `;
  });
};

/**
 * @param {object} stop
 * @param {string} lang - ES, UK, FR
 * @param {string} stop.type_pallets
 * @param {number} stop.equivalent_pallets
 * @param {array}  stop.load_dropoff
 * @param {array}  stop.load_pickup
 * @returns {string} - number of total pallets with type and the pallet size (only custom pallets show size)
 */
export const getPalletsByStop = (stop, lang) => {
  const { type_pallets, real_pallets, load_dropoff = [], load_pickup = [] } = stop;

  const dropoffOrPickup = load_dropoff.length > 0 ? load_dropoff : load_pickup;
  const totalPallets = `${real_pallets} ${t[lang][type_pallets]}`;
  const sizes = dropoffOrPickup.map((load) => `${load.item_length} x ${load.item_width}`);

  return (type_pallets === 'CUSTOM_PALLET' && `${totalPallets} ${sizes}`) || `${totalPallets}`;
};

/**
 * @param {object} j - journey
 * @param {string} country
 * @returns {string}
 */
export const getJourneyPallets = (j, country) => `${j.real_pallets} ${t[country][j.type_pallets]}`;

/**
 * @param {object} SO
 * @param {string} country
 * @returns {string}
 */
export const getShipperOrderPallets = (SO, country) =>
  `${SO.real_pallets} ${t[country][SO.type_pallets]}`;

/**
 * @param {number} cm
 * @returns {number}
 */
export const convertCMtoMeters = (cm) => cm / 100;

/**
 * @param {array} extras
 * @param {string} lang - UK, ES, FR,
 * @returns {array} - array with warnings as strings
 */
export const warningRenderer = (extras, lang) => {
  const extraStrings = {
    LIFTGATE: `${t[lang].LIFTGATE}`,
    SIDE_LOAD: `${t[lang].SIDE_LOAD}`,
    AIRPORT: `${t[lang].AIRPORT}`,
    TOPLOAD: `${t[lang].TOPLOAD}`,
  };

  return extras.map((extra) => extraStrings[extra]);
};

/**
 * @param {array} SO
 * @returns {array} - SO names, 1 or multiple for groupages
 */
export const renderShipperOrderIds = (shipperOrders) => {
  return shipperOrders.map((SO) => ` S${SO.id}`);
};

/**
 * @param {array} shipperOrders
 * @returns {array} - Shippers names, 1 or multiple for groupages
 */
export const renderShipperOrderShippers = (shipperOrders) => {
  return shipperOrders.map((SO) => ` ${SO.shipper.company_name}`);
};
